import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import PropTypes from "prop-types";

import useOnClickOutside from "@/hooks/useOnClickOutside";
import { Loading } from "@/ui-lib";
import { Button } from "@/ui-lib/FormFields";

import styles from "./UncontrolledDropdownMenu.module.scss";

const UncontrolledDropdownMenu = ({
  menuText,
  icon,
  disabled,
  options,
  outline,
  color,
  onBlur,
  wrapperClassName,
  menuClassName,
  iconClassName,
  buttonClassName,
  isTogglePrevented,
  node,
  testId,
}) => {
  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => {
    if (isTogglePrevented) {
      return;
    }

    setIsOpen(!isOpen);

    if (!isOpen) {
      onBlur();
    }
  };

  const onClickItem = async (o) => {
    if (o.onClick) {
      await o.onClick();
      if (o?.hideMenuOnClick) {
        toggle();
      }
    }
    if (o.hideMenuOnClick && !("isLoading" in o)) {
      toggle();
    }
  };

  useOnClickOutside(ref, () => {
    if (isOpen) {
      toggle();
    }
    onBlur();
  });

  return (
    <div
      ref={ref}
      className={cx(styles.dropdown, wrapperClassName)}
      onClick={(e) => {
        e.stopPropagation();
      }}
      aria-hidden="true"
    >
      <Button
        testId={testId}
        disabled={disabled}
        outline={outline}
        color={color}
        className={cx(styles.button, buttonClassName, {
          [styles.buttonWithIcon]: !!icon && menuText === "",
          [styles.buttonWithIconAndText]: !!icon && menuText !== "",
        })}
        onClick={() => toggle()}
      >
        {icon && !menuText ? (
          <FontAwesomeIcon className={cx(styles.icon, iconClassName)} icon={icon} />
        ) : null}
        {menuText ? (
          <>
            {menuText}{" "}
            {icon ? (
              <FontAwesomeIcon className={cx(styles.icon, iconClassName)} icon={icon} />
            ) : null}
          </>
        ) : null}
      </Button>
      {node ? (
        <div className={cx(styles.menu, menuClassName, { [styles.hide]: !isOpen })}>
          {node({ isOpen })}
        </div>
      ) : (
        isOpen &&
        options.filter((o) => !o.isHidden).length > 0 && (
          <div className={cx(styles.menu, menuClassName)}>
            {options.map((o) => (
              <button
                data-testid={o.testId || ""}
                key={o.label}
                type="button"
                onClick={() => onClickItem(o)}
                className={cx(styles.menuItem, o?.menuItemClassName ?? "", {
                  [styles.disabled]: o.disabled,
                  [styles.header]: o.header,
                })}
                disabled={!!o?.disabled || !!o?.isLoading}
              >
                <div className={styles.menuItemTextWrapper}>
                  {o.label}{" "}
                  {o.description && <span className={styles.description}>{o.description}</span>}
                </div>
                {!!o?.isLoading && (
                  <Loading spinnerClassName={styles.spinner} size={1} className={styles.loader} />
                )}
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
};

UncontrolledDropdownMenu.defaultProps = {
  menuText: "",
  icon: null,
  color: "",
  outline: false,
  onBlur: () => null,
  options: [],
  wrapperClassName: "",
  menuClassName: "",
  iconClassName: "",
  buttonClassName: "",
  disabled: false,
  node: null,
  isTogglePrevented: false,
  testId: "",
};

UncontrolledDropdownMenu.propTypes = {
  disabled: PropTypes.bool,
  menuText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  icon: PropTypes.shape(),
  color: PropTypes.string,
  outline: PropTypes.bool,
  onBlur: PropTypes.func,
  wrapperClassName: PropTypes.string,
  menuClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.bool,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
      description: PropTypes.string,
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
      testId: PropTypes.string,
    })
  ),
  node: PropTypes.node,
  isTogglePrevented: PropTypes.bool,
  testId: PropTypes.string,
};

export default UncontrolledDropdownMenu;
